import React, { useEffect, useState } from 'react';
import { RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import runningNotes from '../changelog.json';
import { fetchIncomingChangelog } from '../utils/appUpdate';

/*
 * Shown when a new build is installed and waiting. Names the incoming version by fetching
 * the server's changelog; until that arrives (or if it fails) it says only that an update
 * is ready, never the running version, which is what the old banner got wrong.
 */
const UpdateBanner = ({ onUpdate, onLater }) => {
  const [incoming, setIncoming] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchIncomingChangelog().then((c) => {
      // The server's changelog is the incoming build's by definition; name it even when the
      // version number did not change (a rebuild), so the banner never falls back to "a new version"
      if (alive && c && c.currentVersion) setIncoming(c);
    });
    return () => { alive = false; };
  }, []);

  const notes = incoming?.versions?.[0]?.notes || [];

  return (
    <div className="fixed top-0 left-0 right-0 z-50 animate-slide-down pt-[env(safe-area-inset-top)]">
      <div className="max-w-md mx-auto px-3 pt-3">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="h-1" style={{ backgroundColor: '#1d4ed8' }} aria-hidden="true" />
          <div className="px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
              <RefreshCw size={20} className={busy ? 'animate-spin' : ''} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px] text-gray-900 dark:text-white leading-tight">Update ready</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {!incoming && 'A new version of the app'}
                {incoming && incoming.currentVersion === runningNotes.currentVersion && `Version ${incoming.currentVersion} (rebuilt)`}
                {incoming && incoming.currentVersion !== runningNotes.currentVersion && `Version ${incoming.currentVersion}`}
              </div>
            </div>
            {notes.length > 0 && (
              <button onClick={() => { setOpen((o) => !o); }} aria-expanded={open} aria-label={open ? 'Hide what is new' : 'Show what is new'} className="p-2 -m-1 rounded-lg text-gray-400">
                {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
            )}
          </div>
          {open && notes.length > 0 && (
            <ul className="px-4 pb-3 space-y-1.5 border-t border-gray-100 dark:border-gray-700 pt-3">
              {notes.map((n) => (
                <li key={n} className="text-xs text-gray-700 dark:text-gray-300 leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[0.55em] before:w-1 before:h-1 before:rounded-full before:bg-gray-400">{n}</li>
              ))}
            </ul>
          )}
          <div className="px-4 pb-3 flex gap-2">
            <button onClick={() => { onLater(); }} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700">
              Later
            </button>
            <button
              onClick={() => { setBusy(true); onUpdate(); }}
              disabled={busy}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: '#1d4ed8' }}
            >
              {busy ? 'Updating…' : 'Update now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateBanner;
